import { cs } from '../../../lib/currency';
/**
 * iter307 — Per-enrollment Billing tab.
 *
 * Read-only ledger of every monthly billing cycle for a single
 * enrollment. Pulls from `GET /api/admin/billing/enrollments/{id}/billing-cycles`.
 *
 * Surfaces, per cycle:
 *   • Cycle start / end + billing month
 *   • Amount billed + amount charged (incl. CC surcharge)
 *   • Status (paid / pending / overdue / refunded / void /
 *     pending_refund — the last covers iter307 ACH-in-flight queue)
 *   • Payment method (e.g. Auto-Pay → ACH, Card, Cash, etc.)
 *   • Paid date + Stripe PI for reconciliation
 *
 * Includes a summary strip (cycle count, total billed, total paid, net
 * received after refunds) so admins can spot ledger mismatches at a
 * glance.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { Loader2, AlertCircle, CheckCircle2, Clock, XCircle, RotateCcw, HourglassIcon, Settings2, Pencil, Plus, Trash2 } from 'lucide-react';
import api from '../../../services/api';
import { formatDateOnly } from '../../../utils/timezone';
import { formatPaymentMethod } from '../../../components/admin/invoice/payment_method_helpers';
import SortableTable from '../../../components/ui/sortableTable';
import useTablePreferences from '../../../hooks/useTablePreferences';
import { useAuth } from '../../../context/AuthContext';
import { EditBillingCycleDialog, NewBillingCycleDialog, DeleteBillingCycleDialog, isCycleEditable, isCycleDeletable } from './EnrollmentBillingCycleDialogs';

const fmt = (v) => v == null ? '—' : `${cs()}${Number(v).toFixed(2)}`;

const STATUS_STYLES = {
  paid:             { label: 'Paid',            className: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle2 },
  pending:          { label: 'Pending',         className: 'bg-amber-100 text-amber-800 border-amber-200',       icon: Clock },
  overdue:          { label: 'Overdue',         className: 'bg-red-100 text-red-800 border-red-200',             icon: AlertCircle },
  processing:       { label: 'Processing',      className: 'bg-sky-100 text-sky-800 border-sky-200',             icon: Clock },
  refunded:         { label: 'Refunded',        className: 'bg-purple-100 text-purple-800 border-purple-200',    icon: RotateCcw },
  void:             { label: 'Void',            className: 'bg-slate-200 text-slate-700 border-slate-300',       icon: XCircle },
  pending_refund:   { label: 'Pending Refund',  className: 'bg-orange-100 text-orange-800 border-orange-200',    icon: HourglassIcon },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  const Icon = s.icon;
  return (
    <Badge variant="outline" className={`inline-flex items-center gap-1 ${s.className}`} data-testid={`cycle-status-${status}`}>
      <Icon className="h-3 w-3" /> {s.label}
    </Badge>
  );
};

const MethodBadge = ({ method, autoPayMethod }) => {
  // Auto-charge MIs store payment_method="auto_charge" + auto_pay_method="card"/"ach".
  // Show "Auto-Pay · ACH" or "Auto-Pay · Card" so admins can tell at a glance.
  if (method === 'auto_charge' && autoPayMethod) {
    const inner = formatPaymentMethod(autoPayMethod);
    return (
      <span className="inline-flex items-center gap-1 text-xs" data-testid="cycle-method-autopay">
        <Badge variant="outline" className="bg-sky-100 text-sky-800 border-sky-200">Auto-Pay</Badge>
        <span className="text-slate-400">·</span>
        <Badge variant="outline" className={inner?.className || 'bg-slate-100 text-slate-700'}>{inner?.label || autoPayMethod}</Badge>
      </span>
    );
  }
  const m = formatPaymentMethod(method);
  if (!m) return <span className="text-slate-400 text-xs">—</span>;
  return (
    <Badge variant="outline" className={m.className} data-testid="cycle-method">
      {m.label}
    </Badge>
  );
};

const EnrollmentBillingTab = ({ enrollmentId }) => {
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  // Dialog state
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showNewCycle, setShowNewCycle] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: body } = await api.get(`/admin/billing/enrollments/${enrollmentId}/billing-cycles`);
        if (!cancelled) setData(body);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e?.message || 'Failed to load billing cycles');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [enrollmentId, reloadTick]);

  const refresh = () => setReloadTick((t) => t + 1);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="billing-tab-loading">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200" data-testid="billing-tab-error">
        <CardContent className="p-6 text-sm text-red-700 flex items-center gap-2">
          <AlertCircle className="h-4 w-4" /> {String(error)}
        </CardContent>
      </Card>
    );
  }

  const summary = data?.summary || {};
  const cycles = data?.cycles || [];
  const enr = data?.enrollment || {};

  return (
    <div className="space-y-6" data-testid="enrollment-billing-tab">
      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card data-testid="billing-summary-cycles">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500">Cycles</div>
            <div className="mt-1 text-2xl font-semibold text-slate-900">{summary.cycle_count ?? 0}</div>
            <div className="text-xs text-slate-500 mt-1">
              {summary.paid_count ?? 0} paid · {summary.pending_count ?? 0} pending
            </div>
          </CardContent>
        </Card>
        <Card data-testid="billing-summary-billed">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500">Total Billed</div>
            <div className="mt-1 text-2xl font-semibold text-slate-900 font-mono">{fmt(summary.total_billed)}</div>
          </CardContent>
        </Card>
        <Card data-testid="billing-summary-paid">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500">Total Paid</div>
            <div className="mt-1 text-2xl font-semibold text-emerald-700 font-mono" data-testid="billing-total-paid-value">{fmt(summary.total_paid)}</div>
            {summary.total_refunded > 0 && (
              <div className="text-xs text-purple-700 mt-1" data-testid="billing-total-refunded-value">
                Refunded {fmt(summary.total_refunded)}
                {summary.total_paid_gross && summary.total_paid_gross !== summary.total_paid && (
                  <span className="text-slate-500"> · charged {fmt(summary.total_paid_gross)}</span>
                )}
              </div>
            )}
          </CardContent>
        </Card>
        <Card data-testid="billing-summary-net">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500">Net Received</div>
            <div className="mt-1 text-2xl font-semibold text-slate-900 font-mono">{fmt(summary.net_received)}</div>
            <div className="text-xs text-slate-500 mt-1">
              {enr.frequency || 'monthly'} · NBD {enr.next_billing_date || '—'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cycle table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Billing Cycles</CardTitle>
          {isAdmin && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowNewCycle(true)}
              data-testid="billing-tab-new-cycle-btn"
            >
              <Plus className="h-4 w-4 mr-1" />
              New Cycle
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          <CyclesTable
            cycles={cycles}
            isAdmin={isAdmin}
            onEdit={(cycle) => setEditTarget(cycle)}
            onDelete={(cycle) => setDeleteTarget(cycle)}
          />
        </CardContent>
      </Card>

      {isAdmin && (
        <>
          <EditBillingCycleDialog
            open={!!editTarget}
            onOpenChange={(v) => { if (!v) setEditTarget(null); }}
            enrollmentId={enrollmentId}
            cycle={editTarget}
            onSaved={refresh}
          />
          <NewBillingCycleDialog
            open={showNewCycle}
            onOpenChange={setShowNewCycle}
            enrollmentId={enrollmentId}
            enrollment={enr}
            onCreated={refresh}
          />
          <DeleteBillingCycleDialog
            open={!!deleteTarget}
            onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}
            enrollmentId={enrollmentId}
            cycle={deleteTarget}
            onDeleted={refresh}
          />
        </>
      )}
    </div>
  );
};

// iter307bb — Column registry keyed for visibility lookups. Each column
// gets `sortable: true` (incl. Stripe PI) so admins can sort by any
// column they choose to show. `required` columns are pinned visible
// in the picker (we keep Billing Month + Amount + Status mandatory so
// the table is never empty of context).
const COL_DEFS = {
  billing_month:        { label: 'Billing Month',     required: true,  sortable: true,  type: 'string' },
  period_start:         { label: 'Cycle',             required: false, sortable: true,  type: 'date' },
  amount:               { label: 'Amount',            required: true,  sortable: true,  type: 'numeric', align: 'right' },
  due_date:             { label: 'Due Date',          required: false, sortable: true,  type: 'date' },
  paid_at:              { label: 'Paid On',           required: false, sortable: true,  type: 'date' },
  status:               { label: 'Status',            required: true,  sortable: true,  type: 'string' },
  payment_method:       { label: 'Payment Method',    required: false, sortable: true,  type: 'string' },
  next_payment_date:    { label: 'Next Payment Date', required: false, sortable: true,  type: 'date' },
  stripe_payment_intent:{ label: 'Stripe PI',         required: false, sortable: true,  type: 'string' },
};

// Default visibility — all columns visible except Stripe PI (technical).
const DEFAULT_VISIBLE = {
  billing_month: true, period_start: true, amount: true,
  due_date: true, paid_at: true, status: true,
  payment_method: true, next_payment_date: true,
  stripe_payment_intent: false,
};

const _addDays = (isoDateStr, days) => {
  if (!isoDateStr) return null;
  try {
    const d = new Date(isoDateStr.length === 10 ? isoDateStr + 'T12:00:00Z' : isoDateStr);
    if (Number.isNaN(d.getTime())) return null;
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
  } catch (_e) { return null; }
};

// Column render functions, keyed by column id. Kept separate from the
// COL_DEFS registry so the picker can stay declarative.
const _RENDERERS = {
  billing_month: (c) => (
    <span className="font-medium text-slate-900">
      {c.billing_month || '—'}
      {c.is_prorated && (
        <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-800 border-amber-200 text-[10px]">
          Prorated
        </Badge>
      )}
    </span>
  ),
  period_start: (c) => (
    <span className="text-xs text-slate-600">
      {c.period_start ? formatDateOnly(c.period_start, { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
      {' → '}
      {c.period_end ? formatDateOnly(c.period_end, { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
    </span>
  ),
  amount: (c) => (
    <>
      <div>{fmt(c.amount)}</div>
      {c.cc_surcharge > 0 && (
        <div className="text-[11px] text-slate-400">+{fmt(c.cc_surcharge)} CC</div>
      )}
      {c.original_amount && c.original_amount !== c.amount && (
        <div className="text-[11px] text-emerald-600">
          (-{fmt((c.original_amount || 0) - (c.amount || 0))} discount)
        </div>
      )}
    </>
  ),
  due_date: (c) => (
    <span className="text-xs text-slate-600" data-testid={`cycle-due-${c.id || c.billing_month}`}>
      {c.due_date ? formatDateOnly(c.due_date, { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
    </span>
  ),
  paid_at: (c) => (
    <span className="text-xs text-slate-600">
      {c.paid_date || c.paid_at?.slice(0, 10) || '—'}
    </span>
  ),
  status: (c) => (
    <>
      <StatusBadge status={c.display_status || c.status} />
      {c.refund_amount && (
        <div
          className={`text-[11px] mt-0.5 ${
            c.is_overcharge_corrected || c.refund_type === 'overcharge_correction'
              ? 'text-amber-700'
              : 'text-purple-700'
          }`}
          data-testid={`cycle-refund-chip-${c.mi_id || c.billing_month}`}
          title={
            c.is_overcharge_corrected || c.refund_type === 'overcharge_correction'
              ? `Overcharged ${fmt(c.amount_charged)} on a ${fmt(c.amount)} invoice — refunded ${fmt(c.refund_amount)} to correct. Net paid ${fmt(c.net_paid)}.`
              : `Charged ${fmt(c.amount_charged)} · Refunded ${fmt(c.refund_amount)} · Net paid ${fmt(c.net_paid)}`
          }
        >
          {c.is_overcharge_corrected || c.refund_type === 'overcharge_correction'
            ? `Overcharge corrected −${fmt(c.refund_amount)} · Net ${fmt(c.net_paid)}`
            : `Refunded ${fmt(c.refund_amount)} · Net ${fmt(c.net_paid)}`}
        </div>
      )}
    </>
  ),
  payment_method: (c) => <MethodBadge method={c.payment_method} autoPayMethod={c.auto_pay_method} />,
  next_payment_date: (c) => {
    const next = _addDays(c.period_end, 1);
    return (
      <span className="text-xs text-slate-600" data-testid={`cycle-next-${c.id || c.billing_month}`}>
        {next ? formatDateOnly(next, { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
      </span>
    );
  },
  stripe_payment_intent: (c) => (
    <span className="font-mono text-[11px] text-slate-500">
      {c.stripe_payment_intent
        ? <span title={c.stripe_payment_intent}>{c.stripe_payment_intent.slice(0, 14)}…</span>
        : '—'}
    </span>
  ),
};

// Sort accessors for non-trivial columns (where the raw field doesn't
// reflect how the user sees the data).
const _ACCESSORS = {
  status:               (c) => c.display_status || c.status,
  payment_method:       (c) => c.auto_pay_method || c.payment_method,
  next_payment_date:    (c) => _addDays(c.period_end, 1),
};

// Display order (fixed). Picker controls visibility, NOT order.
const COLUMN_ORDER = [
  'billing_month', 'period_start', 'amount', 'due_date',
  'paid_at', 'status', 'payment_method', 'next_payment_date',
  'stripe_payment_intent',
];

const CyclesTable = ({ cycles, isAdmin = false, onEdit, onDelete }) => {
  const {
    visibleColumns, setVisibleColumns, resetPreferences, loaded,
  } = useTablePreferences('admin_enrollment_billing_cycles', DEFAULT_VISIBLE);
  const [pickerOpen, setPickerOpen] = useState(false);

  const toggleCol = (key) => {
    setVisibleColumns((prev) => ({ ...prev, [key]: !(prev[key] ?? DEFAULT_VISIBLE[key]) }));
  };

  const columns = useMemo(() => {
    if (!loaded) return [];
    const isVisible = (k) => visibleColumns[k] ?? DEFAULT_VISIBLE[k] ?? false;
    const base = COLUMN_ORDER
      .filter((k) => COL_DEFS[k].required || isVisible(k))
      .map((k) => ({
        key: k,
        label: COL_DEFS[k].label,
        type: COL_DEFS[k].type,
        sortable: COL_DEFS[k].sortable,
        align: COL_DEFS[k].align,
        className: k === 'amount' ? 'font-mono' : undefined,
        accessor: _ACCESSORS[k],
        render: _RENDERERS[k],
      }));
    // iter307cd / iter307ce — append Actions column for admins only.
    if (isAdmin) {
      base.push({
        key: 'actions',
        label: '',
        type: 'string',
        sortable: false,
        render: (c) => {
          const editable = isCycleEditable(c);
          const delGuard = isCycleDeletable(c);
          return (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                disabled={!editable}
                onClick={() => onEdit?.(c)}
                title={editable ? 'Edit this billing cycle' : `${c.display_status || c.status} cycles cannot be edited`}
                data-testid={`cycle-edit-btn-${c.mi_id}`}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete?.(c)}
                title={delGuard.reason || (delGuard.ok ? 'Delete (void) this cycle' : 'Cannot delete')}
                className={delGuard.alreadyVoid
                  ? "text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                  : "text-red-600 hover:text-red-700 hover:bg-red-50"}
                data-testid={`cycle-delete-btn-${c.mi_id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          );
        },
      });
    }
    return base;
  }, [visibleColumns, loaded, isAdmin, onEdit, onDelete]);

  if (!cycles || cycles.length === 0) {
    return (
      <div className="p-8 text-center text-sm text-slate-500" data-testid="billing-tab-empty">
        No billing cycles yet. The first cycle will appear after the enrollment is approved and the monthly cron runs.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-end px-3 pt-3">
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" data-testid="billing-cycles-column-picker">
              <Settings2 className="h-4 w-4 mr-2" />
              Columns
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-60">
            <p className="text-xs font-semibold text-slate-700 mb-2">Visible columns</p>
            <div className="space-y-1.5">
              {COLUMN_ORDER.map((k) => {
                const def = COL_DEFS[k];
                return (
                  <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={!!(visibleColumns[k] ?? DEFAULT_VISIBLE[k])}
                      onCheckedChange={() => toggleCol(k)}
                      disabled={def.required}
                      data-testid={`billing-cycles-col-toggle-${k}`}
                    />
                    <span className={def.required ? 'text-slate-400' : ''}>
                      {def.label}{def.required ? ' (required)' : ''}
                    </span>
                  </label>
                );
              })}
            </div>
            <div className="border-t border-slate-200 mt-3 pt-2">
              <Button variant="ghost" size="sm" onClick={resetPreferences} className="w-full text-xs" data-testid="billing-cycles-cols-reset">
                Reset to defaults
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <SortableTable
        data={cycles}
        columns={columns}
        defaultSort={{ field: 'billing_month', direction: 'desc' }}
        pageSize={50}
        getRowKey={(c) => c.mi_id}
        testidPrefix="billing-cycles"
        emptyMessage="No billing cycles yet"
      />
    </div>
  );
};

export default EnrollmentBillingTab;
